/**
 * LR-484 amend mapper.
 *
 * Bridges a booked PositionDetail and the create-trade form's value shape so the
 * SAME trade-entry sections can be reused in an amend modal:
 *  - positionToFormValues(): seed the amend form from a Position (prefill).
 *  - diffAmendPayload(): the PATCH body — ONLY the changed editable "numbers".
 *  - amendChangeRows(): before -> after rows for the approval modal.
 *
 * The editable set mirrors the backend PositionUpdateSerializer.Meta.fields and
 * trade-form-sections AMENDABLE_FIELDS. PositionDetail decimal fields are typed
 * `string` but can arrive as JS numbers at runtime (recurring LR gotcha), so every
 * read is coerced defensively rather than trusting the TS type.
 */
import type { PositionDetail } from '@/types';
import type { TradeFormValues } from '@/features/trades/hooks/useCreateTradeForm';

const str = (v: unknown, fallback = ''): string =>
  v === null || v === undefined ? fallback : String(v);

const num = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/** term_type (open|evergreen|term) -> the form's trade_duration enum. */
const termToDuration = (t?: string | null): string =>
  t === 'term' ? 'TERM' : t === 'evergreen' ? 'EVERGREEN' : 'OPEN';

/**
 * Build the create-form value object from a booked position. Editable numbers are
 * sourced precisely; locked "condition" fields are populated for read-only display
 * from their human-readable PositionDetail columns; genuinely unrecoverable fields
 * (counterparty_id / custodian_id / agreement_id / current_market_price / trade_time /
 * trade_reason_comment / net_settlement_converted_input) default empty — they are
 * locked in amend, so they are neither edited nor submitted.
 */
export function positionToFormValues(p: PositionDetail): TradeFormValues {
  return {
    symbol_id: str(p.symbol_id),
    description: str(p.description),
    sedol: p.sedol,
    isin: str(p.isin),
    security_type: p.security_type,
    currency: str(p.currency, 'USD'),
    booking_center: str(p.booking_center),
    custodian_account: str(p.custodian_account),
    counterparty_name: str(p.counterparty_name),
    counterparty_account: str(p.counterparty_account),
    trade_type: p.trade_type,
    collateral_type: p.collateral_type ?? 'cash',
    // LR-631: carried into the form but never rendered - the pool is not
    // amendable (PositionDetailsSection hides the field in amend). It is here so
    // the Rate Details gate can tell a POOLED fee trade, whose benchmark belongs
    // to its pool, from a pre-LR-631 one that still owns its booking snapshot.
    // Without it amend would offer a Benchmark field the API answers with a 400.
    cash_pool: p.cash_pool ?? null,
    quantity: num(p.quantity) ?? 0,
    gross_price: str(p.gross_price, '0'),
    current_market_price: '', // GAP — not on PositionDetail; locked in amend
    margin_bps: num(p.margin_bps) ?? 10000,
    rate_entry_mode: p.rate_entry_mode ?? 'rebate',
    rate_type: p.rate_type ?? 'floating',
    benchmark_rate: str(p.benchmark_rate, '0.0000'),
    benchmark_name: p.benchmark_name,
    spread_bps: p.spread_bps == null ? undefined : num(p.spread_bps),
    fee_percent: p.fee_percent == null ? undefined : str(p.fee_percent),
    day_count_convention: p.day_count_convention ?? null,
    dividend_passthrough_percent: str(p.dividend_passthrough_percent, '100'),
    trade_reason: str(p.trade_reason),
    trade_reason_comment: '', // GAP — locked in amend
    trade_date: str(p.trade_date).split('T')[0],
    trade_time: '', // GAP — original time not exposed as its own field; locked
    settlement_date: str(p.settlement_date),
    maturity_date: p.maturity_date ?? null,
    term_type: p.term_type ?? 'open',
    notice_period_days: p.notice_period_days ?? null,
    delivery_type: p.delivery_type,
    settlement_venue: p.settlement_venue,
    cash_settlement_place: p.cash_settlement_place ?? undefined,
    securities_settlement_place: p.securities_settlement_place ?? undefined,
    settlement_currency: p.settlement_currency ?? str(p.currency, 'USD'),
    fx_rate: num(p.fx_rate),
    net_settlement_converted_input: undefined, // GAP — operator-only input; locked
    counterparty_id: '', // locked: the counterparty is not amendable
    custodian_id: '', // GAP — locked
    agreement_id: '', // GAP — locked
    comments: str(p.notes),
    trade_duration: termToDuration(p.term_type),
    collateral_isin: str(p.collateral_isin),
    collateral_symbol_id: str(p.collateral_symbol_id),
    collateral_quantity: str(p.collateral_quantity),
    collateral_cob_price: str(p.collateral_cob_price),
    collateral_current_market_price: str(p.collateral_current_market_price),
    collateral_percent: str(p.collateral_percent, '100.00'),
    pre_delivery_party: p.pre_delivery_party ?? '',
    collateral_settlement_place: p.collateral_settlement_place ?? null,
  } as TradeFormValues;
}

/** The amend PATCH body. Backend field names (open_settlement_date, not the form's
 *  settlement_date). Decimal fields go as strings; integers/dates as their native type. */
export interface AmendPayload {
  quantity?: number;
  gross_price?: string;
  margin_bps?: number;
  benchmark_rate?: string;
  spread_bps?: number | null;
  fee_percent?: string | null;
  open_settlement_date?: string;
  maturity_date?: string | null;
  /** LR-484: optional approval note. Not a diffed field — added by the modal on
   *  confirm; the backend writes it to PositionHistory.notes (write-only). */
  comment?: string;
}

type Kind = 'int' | 'decimal' | 'date';

interface FieldDescriptor {
  /** PATCH/payload key (backend field name). */
  key: keyof AmendPayload;
  /** Human label for the before -> after approval row. */
  label: string;
  /** Field name on the create form / TradeFormValues. */
  form: keyof TradeFormValues;
  /** Property on PositionDetail holding the booked value. */
  orig: keyof PositionDetail;
  kind: Kind;
}

// Order = display order in the approval modal.
// LR-495: this registry is the SINGLE SOURCE for the amendable-field contract.
// The diff (diffAmendPayload), the before/after preview (amendChangeRows) and the
// FE section lock (AMENDABLE_FORM_FIELDS, re-exported to the sections as
// AMENDABLE_FIELDS) are all derived from it — there are no parallel lists. The
// only thing it can't derive is the backend PositionUpdateSerializer.Meta.fields
// whitelist (cross-language); a contract test pins the two together.
export const AMEND_FIELDS: readonly FieldDescriptor[] = [
  { key: 'quantity', label: 'Quantity', form: 'quantity', orig: 'quantity', kind: 'int' },
  { key: 'gross_price', label: 'Price', form: 'gross_price', orig: 'gross_price', kind: 'decimal' },
  { key: 'margin_bps', label: 'Margin (bps)', form: 'margin_bps', orig: 'margin_bps', kind: 'int' },
  { key: 'benchmark_rate', label: 'Benchmark rate', form: 'benchmark_rate', orig: 'benchmark_rate', kind: 'decimal' },
  { key: 'spread_bps', label: 'Spread (bps)', form: 'spread_bps', orig: 'spread_bps', kind: 'int' },
  { key: 'fee_percent', label: 'Fee %', form: 'fee_percent', orig: 'fee_percent', kind: 'decimal' },
  { key: 'open_settlement_date', label: 'Settlement date', form: 'settlement_date', orig: 'settlement_date', kind: 'date' },
  { key: 'maturity_date', label: 'Maturity date', form: 'maturity_date', orig: 'maturity_date', kind: 'date' },
] as const;

/**
 * The create-form field names editable in amend mode, DERIVED from AMEND_FIELDS.
 * Re-exported by trade-form-sections/types as AMENDABLE_FIELDS and consumed by
 * isAmendLocked. LR-495 replaced the parallel hand-maintained Set with this
 * derivation, so a new amendable field is one edit to AMEND_FIELDS above (plus
 * the backend whitelist).
 */
export const AMENDABLE_FORM_FIELDS: ReadonlySet<string> = new Set(
  AMEND_FIELDS.map((d) => d.form),
);

/** Backend PATCH field names this registry expects to be amendable — the FE half
 *  of the cross-language contract with PositionUpdateSerializer.Meta.fields. */
export const AMEND_PAYLOAD_KEYS: readonly string[] = AMEND_FIELDS.map((d) => d.key);

/** A field changed? Money/integers compare NUMERICALLY ("180.00" === "180" === 180,
 *  so trailing-zero / type drift never reads as a change); dates compare as strings. */
const hasChanged = (kind: Kind, formVal: unknown, origVal: unknown): boolean => {
  if (kind === 'date') return str(formVal) !== str(origVal);
  const a = num(formVal);
  const b = num(origVal);
  if (a === undefined && b === undefined) return false;
  return a !== b;
};

/** Format a field value for the PATCH body in its target wire type. */
const toPayloadValue = (kind: Kind, formVal: unknown): number | string | null => {
  if (kind === 'int') return num(formVal) ?? null;
  // decimal + date go as strings; empty -> null so it clears server-side.
  return formVal === null || formVal === undefined || formVal === '' ? null : str(formVal);
};

/** The PATCH body: only the editable fields that actually changed vs the booked position. */
export function diffAmendPayload(original: PositionDetail, values: TradeFormValues): AmendPayload {
  const out: Record<string, number | string | null> = {};
  for (const d of AMEND_FIELDS) {
    if (hasChanged(d.kind, values[d.form], original[d.orig])) {
      out[d.key] = toPayloadValue(d.kind, values[d.form]);
    }
  }
  return out as AmendPayload;
}

export interface AmendChangeRow {
  label: string;
  before: string;
  after: string;
}

/** Display a value for the before/after column. Empty string reads as "—" too
 *  (not just null/undefined), so a CLEARED field renders "X → —" rather than
 *  "X → " (LR-484 r1 L3). */
const toDisplay = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '—' : String(v);

/** before -> after rows (changed fields only) for the approval modal. */
export function amendChangeRows(original: PositionDetail, values: TradeFormValues): AmendChangeRow[] {
  const rows: AmendChangeRow[] = [];
  for (const d of AMEND_FIELDS) {
    if (hasChanged(d.kind, values[d.form], original[d.orig])) {
      rows.push({
        label: d.label,
        before: toDisplay(original[d.orig]),
        after: toDisplay(values[d.form]),
      });
    }
  }
  return rows;
}
